/**
 * Inbox's two views, and the addresses that open them.
 *
 * Messages and Updates share one tab: both lists are what the team has sent
 * you. Which view is open lives in the query, so a link can open either one
 * and the shell can tell them apart.
 *
 * The old `/one/updates` address forwards to `?view=updates` in
 * `next.config.mjs`.
 */

export type InboxSegment = "messages" | "updates";

const VIEW = "view";

/** Anything but `view=updates` is Messages, the inbox's default. */
export function inboxSegment(
  params: Pick<URLSearchParams, "get">,
): InboxSegment {
  return params.get(VIEW) === "updates" ? "updates" : "messages";
}

/**
 * The inbox showing `segment`, keeping the rest of the current query.
 *
 * What belongs to a view does not follow you out of it: an open thread is
 * dropped on the way to Updates, and a single linked update on the way to
 * Messages, so coming back shows the whole feed. A token never rides along on
 * a link inside the portal, which is the rule `portalHref` follows too.
 */
export function inboxSegmentHref(
  segment: InboxSegment,
  current: URLSearchParams = new URLSearchParams(),
  conversation?: string,
): string {
  const kept = new URLSearchParams(current.toString());
  for (const key of [VIEW, "update", "conversation", "token"]) kept.delete(key);
  const params = new URLSearchParams(
    segment === "updates"
      ? { [VIEW]: "updates" }
      : conversation
        ? { conversation }
        : {},
  );
  for (const [key, value] of kept) params.append(key, value);
  return params.size ? `/one/inbox?${params}` : "/one/inbox";
}
